/*!

=========================================================
* Argon Dashboard PRO React - v1.2.5
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
// nodejs library to set properties for components
import PropTypes from "prop-types";
// reactstrap components
import { Card, CardBody, CardTitle, Container, Row, Col } from "reactstrap";
import { formatValues } from "components/Common/commonFunctions";

// Helper function to format currency with proper negative sign placement
const formatCurrency = (value, showSymbol = true) => {
  const numValue = Number(value) || 0;
  const absValue = Math.abs(numValue);
  const formattedValue = formatValues(absValue);
  
  if (!showSymbol) return formattedValue;
  
  // Return with proper negative sign placement: -$1234.56 instead of $-1234.56
  return numValue < 0 ? `-$${formattedValue}` : `$${formattedValue}`;
};

// Helper function to format percentage
const formatPercentage = (value) => {
  const numValue = Number(value) || 0;
  return `${numValue.toFixed(2)}%`;
};

// Helper function to format PNL with color styling
const formatPNL = (value) => {
  const numValue = Number(value) || 0;
  const formattedValue = formatCurrency(numValue);
  
  // You can add color styling here if needed
  return formattedValue;
};

function CardsHeader({ name, parentName, staticInfo = "", kpiData }) {
  // Responsive column classes
  const getColClass = () => {
    // Adjust column sizes for different screen sizes to prevent icon wrapping
    return "col-12 col-sm-6 col-md-4 col-lg-3 col-xl mb-3";
  };

  return (
    <>
      <div className="dark-header pb-6">
        <div className="geometric-bg">
          <div className="geo-square square-1"></div>
          <div className="geo-square square-2"></div>
          <div className="geo-circle"></div>
        </div>
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center py-4">
              <Col lg="6" xs="7">
                <h6 className="h2 text-white d-inline-block mb-0">
                  {name}{" "}
                  <span className="text-danger small font-weight-bold">
                    {staticInfo}
                  </span>
                </h6>
              </Col>
            </Row>

            <Row className="justify-content-center">
              {parentName !== "Admin" && (
                <Col md="6" className={getColClass()}>
                  <Card className="card-stats h-100">
                    <CardBody className="d-flex justify-content-between">
                      <div className="flex-grow-1" style={{ minWidth: 0 }}> {/* minWidth:0 allows text truncation */}
                        <CardTitle
                          tag="h5"
                          className="text-uppercase text-muted mb-0 text-truncate"
                        >
                          Ad Requests
                        </CardTitle>
                        <span className="h2 font-weight-bold mb-0">
                          {formatValues(
                            kpiData?.publisherValues?.adRequests || 0,
                          )}
                        </span>
                      </div>
                      <div className="ml-3 flex-shrink-0"> {/* flex-shrink-0 prevents icon from shrinking */}
                        <div
                          className="icon icon-shape text-white rounded-circle shadow d-flex align-items-center justify-content-center"
                          style={{ backgroundColor: "#8FA6A0", width: "48px", height: "48px" }}
                        >
                          <i className="fa fa-retweet" />
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              )}
              
              <Col md="6" className={getColClass()}>
                <Card className="card-stats h-100">
                  <CardBody className="d-flex justify-content-between">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <CardTitle
                        tag="h5"
                        className="text-uppercase text-muted mb-0 text-truncate"
                      >
                        {parentName === "Admin"
                          ? "Net Revenue"
                          : "Impressions"}
                      </CardTitle>
                      <span className="h2 font-weight-bold mb-0 text-truncate d-block">
                        {parentName === "Admin" 
                          ? formatCurrency(kpiData?.adminValues?.netRevenue)
                          : formatValues(kpiData?.publisherValues?.impression || 0)}
                      </span>
                    </div>
                    <div className="ml-3 flex-shrink-0">
                      <div
                        className="icon icon-shape text-white rounded-circle shadow d-flex align-items-center justify-content-center"
                        style={{ backgroundColor: "#0c0c0c", width: "48px", height: "48px" }}
                      >
                        <i className="fa fa-thumbs-up" />
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              
              <Col md="6" className={getColClass()}>
                <Card className="card-stats h-100">
                  <CardBody className="d-flex  justify-content-between">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <CardTitle
                        tag="h5"
                        className="text-uppercase text-muted mb-0 text-truncate"
                      >
                        {parentName === "Admin"
                          ? "Publisher Payout"
                          : "Fill Rate"}
                      </CardTitle>
                      <span className="h2 font-weight-bold mb-0 text-truncate d-block">
                        {parentName === "Admin"
                          ? formatCurrency(kpiData?.adminValues?.publisherPayout)
                          : formatPercentage(kpiData?.publisherValues?.fillRate)}
                      </span>
                    </div>
                    <div className="ml-3 flex-shrink-0">
                      <div
                        className="icon icon-shape text-white rounded-circle shadow d-flex align-items-center justify-content-center"
                        style={{ backgroundColor: "#d8534c", width: "48px", height: "48px" }}
                      >
                        <i className="fa fa-area-chart" />
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              
              <Col md="6" className={getColClass()}>
                <Card className="card-stats h-100">
                  <CardBody className="d-flex justify-content-between">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <CardTitle
                        tag="h5"
                        className="text-uppercase text-muted mb-0 text-truncate"
                      >
                        {parentName === "Admin" ? "PNL" : "ERPM"}
                      </CardTitle>
                      <span 
                        className={`h2 font-weight-bold mb-0 text-truncate d-block ${
                          parentName === "Admin" && Number(kpiData?.adminValues?.pnl) < 0 
                            ? "text-danger" 
                            : parentName === "Admin" && Number(kpiData?.adminValues?.pnl) > 0
                            ? "text-success"
                            : ""
                        }`}
                      >
                        {parentName === "Admin"
                          ? formatPNL(kpiData?.adminValues?.pnl)
                          : formatValues(kpiData?.publisherValues?.erpm)}
                      </span>
                    </div>
                    <div className="ml-3 flex-shrink-0">
                      <div
                        className="icon icon-shape text-white rounded-circle shadow d-flex justify-content-center"
                        style={{ backgroundColor: "#8FA6A0", width: "48px", height: "48px" }}
                      >
                        <i className="ni ni-money-coins" />
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              
              <Col md="6" className={getColClass()}>
                <Card className="card-stats h-100">
                  <CardBody className="d-flex  justify-content-between">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <CardTitle
                        tag="h5"
                        className="text-uppercase text-muted mb-0 text-truncate"
                      >
                        {parentName === "Admin" ? "PNL%" : "REVENUE"}
                      </CardTitle>
                      <span 
                        className={`h2 font-weight-bold mb-0 text-truncate d-block ${
                          parentName === "Admin" && Number(kpiData?.adminValues?.pnlPercent) < 0 
                            ? "text-danger" 
                            : parentName === "Admin" && Number(kpiData?.adminValues?.pnlPercent) > 0
                            ? "text-success"
                            : ""
                        }`}
                      >
                        {parentName === "Admin"
                          ? `${Number(kpiData?.adminValues?.pnlPercent || 0).toFixed(2)}%`
                          : formatCurrency(kpiData?.publisherValues?.revenue)}
                      </span>
                    </div>
                    <div className="ml-3 flex-shrink-0">
                      <div
                        className="icon icon-shape text-white rounded-circle shadow d-flex align-items-top justify-content-center"
                        style={{ backgroundColor: "#84BAC0", width: "48px", height: "48px" }}
                      >
                        <i className="ni ni-chart-bar-32" />
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    </>
  );
}

CardsHeader.propTypes = {
  name: PropTypes.string,
  parentName: PropTypes.string,
  staticInfo: PropTypes.string,
  kpiData: PropTypes.shape({
    adminValues: PropTypes.shape({
      netRevenue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      pnl: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      pnlPercent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      publisherPayout: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    publisherValues: PropTypes.shape({
      adRequests: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      impression: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      fillRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      erpm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      revenue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
};

export default CardsHeader;